/* eslint-disable react-refresh/only-export-components */
import { type CSSProperties, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { BANNER_H, BANNER_W, type BannerDesign, type BannerLayer } from '@/features/studio/model/bannerLayers';

/**
 * The one banner renderer. The Banner Studio canvas, every context preview,
 * and the live profile surfaces all render layer data through this component,
 * so a published banner is pixel-identical to the editor — including real
 * backdrop-blur glass, which a flattened export could only fake.
 *
 * Layers are authored in 1600×600 design space; this renders them at whatever
 * size the container is, positioning by percentage so scale is free.
 */
export function BannerLayerRender({
  design,
  className,
  children,
}: {
  design: BannerDesign;
  className?: string;
  /** Optional per-layer overlay factory (editor uses it for selection chrome). */
  children?: (layer: BannerLayer, style: CSSProperties) => ReactNode;
}) {
  return (
    <div className={cn('pointer-events-none relative h-full w-full overflow-hidden [container-type:size]', className)}>
      {design.layers.map((layer) => {
        if (layer.hidden) return null;
        const style = layerBoxStyle(layer);
        return (
          <div key={layer.id} style={style}>
            <LayerContent layer={layer} />
            {children?.(layer, style)}
          </div>
        );
      })}
    </div>
  );
}

/** Percentage box + blend/opacity for a layer (relative to design space). */
export function layerBoxStyle(layer: BannerLayer): CSSProperties {
  return {
    position: 'absolute',
    left: `${(layer.x / BANNER_W) * 100}%`,
    top: `${(layer.y / BANNER_H) * 100}%`,
    width: `${(layer.w / BANNER_W) * 100}%`,
    height: `${(layer.h / BANNER_H) * 100}%`,
    opacity: layer.opacity,
    mixBlendMode: layer.blend === 'normal' ? undefined : layer.blend,
  };
}

function LayerContent({ layer }: { layer: BannerLayer }) {
  switch (layer.kind) {
    case 'image':
      return layer.url ? (
        <div
          className="h-full w-full"
          style={{
            backgroundImage: `url(${layer.url})`,
            backgroundSize: layer.fit,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: radiusPct(layer.radius),
          }}
        />
      ) : (
        <div className="h-full w-full rounded-md border border-dashed border-white/25 bg-white/[0.04]" />
      );
    case 'shape':
      return (
        <div
          className="h-full w-full"
          style={{
            background: layer.fill,
            borderRadius: layer.shape === 'ellipse' ? '50%' : radiusPct(layer.radius),
          }}
        />
      );
    case 'gradient':
      return (
        <div
          className="h-full w-full"
          style={{ background: `linear-gradient(${layer.angle}deg, ${layer.from}, ${layer.to})` }}
        />
      );
    case 'glass':
      return (
        <div
          className="h-full w-full"
          style={{
            backdropFilter: `blur(${layer.blur}px) saturate(1.2)`,
            WebkitBackdropFilter: `blur(${layer.blur}px) saturate(1.2)`,
            background: `color-mix(in srgb, ${layer.tint} ${Math.round(layer.tintOpacity * 100)}%, transparent)`,
            borderRadius: radiusPct(layer.radius),
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        />
      );
    case 'text':
      return (
        <div
          className="flex h-full w-full items-center overflow-hidden whitespace-nowrap"
          style={{
            justifyContent: layer.align === 'left' ? 'flex-start' : layer.align === 'right' ? 'flex-end' : 'center',
            color: layer.color,
            fontWeight: layer.weight,
            // Font size scales with the rendered box height (size/BANNER_H of container height ≈ design px).
            fontSize: `${(layer.size / BANNER_H) * 100}cqh`,
            fontFamily: "'Inter', system-ui, sans-serif",
            letterSpacing: '-0.02em',
            textShadow: layer.shadow ? '0 4px 24px rgba(0,0,0,0.55)' : undefined,
          }}
        >
          {layer.text}
        </div>
      );
  }
}

/**
 * Cover-fit wrapper — renders the 1600×600 design the way every real surface
 * shows it: scaled to cover the container at the design's 8:3 aspect, cropped,
 * and positioned by the focal point (background-position semantics, so
 * focal 0..100 maps edge-to-edge of the overflow). This is what makes
 * drag-to-reframe truthful across every context.
 */
export function BannerCoverRender({
  design,
  focal = { x: 50, y: 50 },
  className,
}: {
  design: BannerDesign;
  focal?: { x: number; y: number };
  className?: string;
}) {
  return (
    <div className={cn('absolute inset-0 overflow-hidden', className)}>
      <div
        className="absolute aspect-[8/3] min-h-full min-w-full"
        style={{
          left: `${focal.x}%`,
          top: `${focal.y}%`,
          transform: `translate(-${focal.x}%, -${focal.y}%)`,
        }}
      >
        <BannerLayerRender design={design} />
      </div>
    </div>
  );
}

function radiusPct(r: number): string {
  // Design-px radius → rendered via container-relative units would drift; a
  // simple px value at typical render sizes reads correctly (≤50% clamp).
  return `${Math.max(0, r) / 8}cqw`;
}
